import { resolveOtherInfoObj } from '@moko/utils';
import { useContext, useEffect, useRef, useState } from 'react';
import { IMetadata, getMetadata } from '../../services/metadataService';
import { IGroup, getGroup } from '../../services/groupService';
import {
  ICreateContractorRequest,
  IUpdateContractorRequest,
  createContractor,
  editContractor,
  getContractor,
} from '../../services/contractorService';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Controller, useForm } from 'react-hook-form';
import WorksV2 from '../works/WorksV2';
import ContractorAvailability from './ContractorAvailability';
import {
  createContractorAvailabilityRule,
  type ContractorAvailabilityDraft,
} from '../../services/contractorAvailabilityRuleService';
import { useLocation, useNavigate } from 'react-router-dom';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import { getWorkAssignment } from '../../services/workService';
import { WizardSteps, useWizard, type WizardStep } from '@moko/ui';
import { Features, useFeatureFlag } from '../../libs/featureAccess';
import OtherInfo from '../../common/OtherInfoPortal';
import {
  Button,
  Field,
  Input,
  Spinner,
  Tab,
  TabList,
  Tooltip,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { Add24Regular, Search16Regular } from '@fluentui/react-icons';
import AppContext from '../AppContext';

interface ContractorDetailsForm {
  name: string;
  code: string;
}

const DETAILS_MAX_WIDTH = 760;

type ContractorTab = 'details' | 'availability' | 'assignments';

const ContractorV2 = () => {
  const query = new URLSearchParams(useLocation().search);
  const routeId = Number.parseInt(query.get('id') ?? '');
  const contractorId = Number.isNaN(routeId) ? undefined : routeId;
  const isNew = contractorId === undefined;

  const navigate = useNavigate();
  const context = useContext(AppContext);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [metadata, setMetadata] = useState<IMetadata>();
  const [otherInfo, setOtherInfo] = useState<Record<string, unknown>>({});
  const [group, setGroup] = useState<IGroup>();
  const [worksIds, setWorksIds] = useState<number[]>([]);
  const [draftRules, setDraftRules] = useState<ContractorAvailabilityDraft[]>(
    []
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editTab, setEditTab] = useState<ContractorTab>('details');

  const isSaved = useRef(false);

  const detailsForm = useForm<ContractorDetailsForm>({
    mode: 'onChange',
    defaultValues: { name: '', code: '' },
  });
  const detailsValid = detailsForm.formState.isValid;
  const { isEnabled: showAvailability } = useFeatureFlag(
    Features.FEATURE_CONTRACTOR_AVAILABILITY
  );

  const [
    isUserSelectorOpen,
    { setTrue: showUserSelector, setFalse: hideUserSelector },
  ] = useBoolean(false);

  const optionalStepCompleted: WizardStep['isCompleted'] = ({
    stepIndex,
    activeIndex,
  }) => activeIndex >= stepIndex;

  const steps: WizardStep[] = [
    {
      key: 'details',
      label: 'Enter contractor details',
      isCompleted: detailsValid,
      content: null,
    },
    ...(showAvailability
      ? [
          {
            key: 'availability',
            label: 'Availability',
            lockedTooltip: 'Complete the contractor details first',
            isCompleted: optionalStepCompleted,
            content: null,
          } satisfies WizardStep,
        ]
      : []),
    {
      key: 'assignments',
      label: 'Assignments',
      lockedTooltip: 'Complete the contractor details first',
      isCompleted: optionalStepCompleted,
      content: null,
    },
  ];
  const wizard = useWizard({ steps });
  const resolvedEditTab: ContractorTab =
    editTab === 'availability' && !showAvailability ? 'details' : editTab;
  const activeSection = isNew
    ? (wizard.activeKey as ContractorTab)
    : resolvedEditTab;

  useEffect(() => {
    if (isNew) return;
    const abortController = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const contractor: any = await getContractor(
          abortController,
          contractorId
        );
        // internalFetch resolves to `undefined` on an aborted request (e.g.
        // StrictMode double-mount). Bail out before touching properties.
        if (abortController.signal.aborted || !contractor) return;
        if (contractor.groupId) {
          const g = await getGroup(abortController, contractor.groupId);
          if (g) setGroup({ id: contractor.groupId, name: g.name } as IGroup);
        }
        const md = await getMetadata(abortController, 'Contractor');
        if (abortController.signal.aborted) return;
        setMetadata(md);
        setOtherInfo(resolveOtherInfoObj(contractor.otherInfo) ?? {});
        // Note: assignments in edit mode are owned by the /p/workAssignment
        // endpoint and shown via the grid, so we intentionally don't seed
        // worksIds here -- edit Save never sends it.
        detailsForm.reset({
          name: contractor.name ?? '',
          code: contractor.code ?? '',
        });
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError') return;
        setErrorMessage((error as { message?: string })?.message ?? '');
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    })();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId, isNew]);

  // New mode still needs the Contractor metadata for the OtherInfo section.
  useEffect(() => {
    if (!isNew) return;
    const abortController = new AbortController();
    getMetadata(abortController, 'Contractor')
      .then(setMetadata)
      .catch((error: unknown) => {
        if ((error as { name?: string })?.name === 'AbortError') return;
        setErrorMessage((error as { message?: string })?.message ?? '');
      });
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const handleCreate = detailsForm.handleSubmit(async (values) => {
    try {
      setSaving(true);
      setIsInProgress(true);
      const abortController = new AbortController();
      const data: ICreateContractorRequest = {
        name: values.name,
        code: values.code,
        worksIds,
        otherInfo: JSON.stringify(otherInfo),
        assignmentsDescription: '',
        groupId: group?.id,
      };
      const res = await createContractor(abortController, data);
      for (const rule of draftRules) {
        await createContractorAvailabilityRule(abortController, {
          contractorId: res.contractorId,
          daysOfWeek: rule.daysOfWeek,
          startTime: rule.startTime,
          endTime: rule.endTime,
          timezoneId: rule.timezoneId,
          effectiveFrom: rule.effectiveFrom,
          effectiveTo: rule.effectiveTo,
          status: rule.status,
        });
      }
      isSaved.current = true;
      setSuccessMessage(`Contractor with id: ${res.contractorId} created.`);
      navigate('/contractors', { replace: true });
    } catch (error) {
      setErrorMessage((error as { message?: string })?.message ?? '');
    } finally {
      setSaving(false);
      setIsInProgress(false);
    }
  });

  const handleUpdate = detailsForm.handleSubmit(async (values) => {
    if (!contractorId) return;
    try {
      setSaving(true);
      setIsInProgress(true);
      const abortController = new AbortController();
      // The edit PUT REPLACES the assignment set, so it must carry the FULL
      // current set. The grid only holds one page in memory, so fetch the
      // complete assignment list once here to build worksIds.
      const assignments = await getWorkAssignment(
        abortController,
        contractorId,
        1000,
        1
      );
      const currentWorksIds = (assignments?.items ?? [])
        .map((w) => (w as { workId?: number; id: number }).workId ?? w.id)
        .filter((id): id is number => id != null);
      const data: IUpdateContractorRequest = {
        name: values.name,
        code: values.code,
        worksIds: currentWorksIds,
        otherInfo: JSON.stringify(otherInfo),
        groupId: group?.id,
      };
      await editContractor(abortController, contractorId, data);
      isSaved.current = true;
      setSuccessMessage(
        `Contractor with id: ${contractorId} successfully updated.`
      );
    } catch (error) {
      setErrorMessage((error as { message?: string })?.message ?? '');
    } finally {
      setSaving(false);
      setIsInProgress(false);
    }
  });

  const cancelButton = (
    <Button appearance="secondary" onClick={() => navigate('/contractors')}>
      Cancel
    </Button>
  );

  const createActions = (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
      {cancelButton}
      {!wizard.isFirstStep && (
        <Button appearance="transparent" onClick={wizard.goBack}>
          Back
        </Button>
      )}
      {!wizard.isLastStep ? (
        wizard.canGoNext ? (
          <Button appearance="primary" onClick={() => wizard.goNext()}>
            Continue
          </Button>
        ) : (
          <Tooltip
            content="Complete the required contractor details first"
            relationship="description"
            withArrow
          >
            <span style={{ display: 'inline-flex' }}>
              <Button appearance="primary" disabled>
                Continue
              </Button>
            </span>
          </Tooltip>
        )
      ) : (
        <Button
          appearance="primary"
          icon={saving ? <Spinner size="tiny" /> : <Add24Regular />}
          disabled={!detailsValid || saving}
          onClick={handleCreate}
        >
          Create
        </Button>
      )}
    </Stack>
  );

  const editDetailsActions = (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
      {cancelButton}
      <Button
        appearance="primary"
        icon={saving ? <Spinner size="tiny" /> : undefined}
        disabled={!detailsValid || saving}
        onClick={handleUpdate}
      >
        Save
      </Button>
    </Stack>
  );

  const onEditTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setEditTab(String(data.value) as ContractorTab);
  };

  const detailsContent = (
    <Stack
      tokens={{ childrenGap: 16 }}
      style={{ width: '100%', maxWidth: 500 }}
    >
      {!isNew && editDetailsActions}
      <Controller
        control={detailsForm.control}
        name="name"
        rules={{
          required: 'Name is required',
          validate: (v) => v.trim() !== '',
        }}
        render={({ field, fieldState }) => (
          <Field
            label="Name"
            required
            orientation="horizontal"
            validationState={fieldState.error ? 'error' : 'none'}
            validationMessage={fieldState.error?.message}
          >
            <Input
              value={field.value}
              onChange={(_ev, data) => field.onChange(data.value ?? '')}
              onBlur={field.onBlur}
              placeholder="Contractor name"
            />
          </Field>
        )}
      />
      <Controller
        control={detailsForm.control}
        name="code"
        rules={{
          required: 'Code is required',
          validate: (v) => v.trim() !== '',
        }}
        render={({ field, fieldState }) => (
          <Field
            label="Code"
            required
            orientation="horizontal"
            validationState={fieldState.error ? 'error' : 'none'}
            validationMessage={fieldState.error?.message}
          >
            <Input
              value={field.value}
              onChange={(_ev, data) => field.onChange(data.value ?? '')}
              onBlur={field.onBlur}
              placeholder="Contractor code"
            />
          </Field>
        )}
      />
      <Field label="Group" orientation="horizontal">
        <Input
          value={group?.name ?? ''}
          readOnly
          contentAfter={
            <Search16Regular
              style={{ cursor: 'pointer' }}
              onClick={() => showUserSelector()}
            />
          }
        />
      </Field>
      <UserOrGroupSelector
        userOrGroupOnly={'group'}
        isOpen={isUserSelectorOpen}
        onUserOrGroupSelected={(
          id: number,
          _isGroup: boolean,
          username?: string
        ) => {
          setGroup({ id, name: username ?? '' } as IGroup);
          hideUserSelector();
        }}
        hideUsersAndGroupsSelector={hideUserSelector}
      />

      {metadata && (
        <OtherInfo
          metadata={metadata}
          otherInfo={JSON.stringify(otherInfo)}
          isSaved={isSaved}
          onOtherInfoChanged={(newValue: any) => setOtherInfo(newValue)}
        />
      )}
    </Stack>
  );

  const assignmentsContent = isNew ? (
    <WorksV2
      hideExtraColumns={true}
      draftWorkIds={worksIds}
      onDraftChange={setWorksIds}
      TabCaption="Contractors"
    />
  ) : (
    <WorksV2
      contractorId={contractorId}
      hideEditButtons={true}
      hideExtraColumns={true}
      showWorkAssignment={true}
      TabCaption="Contractors"
    />
  );

  // Drive the single page title from the layout (context.selectedTab) instead
  // of rendering a second heading. Re-assert on step/name change because the
  // edit-mode Assignments grid (WorksV2) also sets the tab on mount.
  const contractorName = detailsForm.watch('name');
  useEffect(() => {
    const title = isNew
      ? 'New Contractor'
      : `Contractor ${contractorName || contractorId}`;
    context.setSelectedTab(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, contractorName, contractorId, activeSection]);

  return (
    <Stack tokens={{ childrenGap: 24 }} style={{ padding: '4px 2px' }}>
      {isNew ? (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          wrap
          tokens={{ childrenGap: 16 }}
          style={{ maxWidth: DETAILS_MAX_WIDTH }}
        >
          <WizardSteps
            steps={steps}
            activeKey={wizard.activeKey}
            completedByIndex={wizard.completedByIndex}
            canNavigateTo={wizard.canNavigateTo}
            onStepClick={wizard.goTo}
          />
          {createActions}
        </Stack>
      ) : (
        <TabList selectedValue={resolvedEditTab} onTabSelect={onEditTabSelect}>
          <Tab value="details">Details</Tab>
          {showAvailability && <Tab value="availability">Availability</Tab>}
          <Tab value="assignments">Assignments</Tab>
        </TabList>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner label="Loading..." />
        </div>
      ) : activeSection === 'details' ? (
        <div style={{ maxWidth: DETAILS_MAX_WIDTH }}>{detailsContent}</div>
      ) : activeSection === 'availability' ? (
        <ContractorAvailability
          contractorId={contractorId}
          draftRules={draftRules}
          onDraftChange={setDraftRules}
        />
      ) : (
        assignmentsContent
      )}
    </Stack>
  );
};

export default ContractorV2;
